import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import AdminLayout from '../../components/admin/AdminLayout';
import { useToast } from '../../contexts/ToastContext';
import { useNavigate } from 'react-router';
import {
  getAdminWorkshops,
  createWorkshop,
  updateWorkshop,
  deleteWorkshop,
  generateWorkshopAI,
  getTrainers,
} from '../../api/client';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import {
  Search,
  Plus,
  Edit3,
  Trash2,
  Loader2,
  X,
  Sparkles,
  Clock,
  Calendar,
  Users,
  IndianRupee,
  Eye,
} from 'lucide-react';

interface Trainer {
  id: number;
  name: string;
}

interface Workshop {
  id: number;
  title: string;
  slug: string;
  description: string;
  duration: string;
  start_date: string;
  end_date: string;
  price: number;
  max_seats: number;
  seats_filled: number;
  status: string;
  trainer_id?: number;
  trainer_name?: string;
}

const emptyForm = {
  title: '',
  slug: '',
  description: '',
  duration: '',
  start_date: '',
  end_date: '',
  price: '',
  max_seats: '50',
  status: 'upcoming',
  trainer_id: '',
};

export default function AdminWorkshops(): React.JSX.Element {
  const toast = useToast();
  const navigate = useNavigate();
  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<Workshop | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [trainers, setTrainers] = useState<Trainer[]>([]);

  // AI modal
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiTopic, setAiTopic] = useState('');
  const [aiLoading, setAiLoading] = useState(false);

  const fetchWorkshops = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (search.trim()) params.search = search.trim();
      const res = await getAdminWorkshops(params);
      setWorkshops(Array.isArray(res.data) ? res.data : res.data?.data || []);
    } catch (err: any) {
      toast.show(err.message || 'Failed to load workshops', 'error');
    } finally {
      setLoading(false);
    }
  }, [search, toast]);

  const fetchTrainers = useCallback(async () => {
    try {
      const res = await getTrainers();
      const list = res.data?.trainers || [];
      setTrainers(list.map((t: any) => ({ id: t.id, name: t.name || t.user_id })));
    } catch {
      // silently fail
    }
  }, []);

  useEffect(() => {
    fetchWorkshops();
    fetchTrainers();
  }, [fetchWorkshops, fetchTrainers]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm });
    setIsModalOpen(true);
  };

  const openEdit = (w: Workshop) => {
    setEditing(w);
    setForm({
      title: w.title,
      slug: w.slug,
      description: w.description || '',
      duration: w.duration || '',
      start_date: w.start_date ? w.start_date.slice(0, 16) : '',
      end_date: w.end_date ? w.end_date.slice(0, 16) : '',
      price: String(w.price),
      max_seats: String(w.max_seats),
      status: w.status,
      trainer_id: w.trainer_id ? String(w.trainer_id) : '',
    });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.slug.trim()) {
      toast.show('Title and slug are required', 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price) || 0,
        max_seats: parseInt(form.max_seats) || 50,
      };
      if (editing) {
        await updateWorkshop(editing.id, payload);
        toast.show('Workshop updated', 'success');
      } else {
        await createWorkshop(payload);
        toast.show('Workshop created', 'success');
      }
      setIsModalOpen(false);
      fetchWorkshops();
    } catch (err: any) {
      toast.show(err.message || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Delete this workshop?')) return;
    setDeletingId(id);
    try {
      await deleteWorkshop(id);
      toast.show('Workshop deleted', 'success');
      fetchWorkshops();
    } catch (err: any) {
      toast.show(err.message || 'Delete failed', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const handleAIGenerate = async () => {
    if (!aiTopic.trim()) {
      toast.show('Enter a topic first', 'error');
      return;
    }
    setAiLoading(true);
    try {
      const res = await generateWorkshopAI(aiTopic.trim());
      const data = res.data || res;
      setForm((prev) => ({
        ...prev,
        title: data.title || prev.title,
        slug: data.slug || prev.slug,
        description: data.description || prev.description,
        duration: data.duration || prev.duration,
        max_seats: String(data.max_seats || prev.max_seats),
        price: String(data.price || prev.price),
      }));
      setAiModalOpen(false);
      setAiTopic('');
      toast.show('AI content generated! Review and save.', 'success');
      if (!isModalOpen) setIsModalOpen(true);
    } catch (err: any) {
      toast.show(err.message || 'AI generation failed', 'error');
    } finally {
      setAiLoading(false);
    }
  };

  const statusColor = (status: string) => {
    switch (status) {
      case 'live': return 'bg-green-50 text-green-700';
      case 'upcoming': return 'bg-blue-50 text-blue-700';
      case 'completed': return 'bg-slate-100 text-slate-600';
      case 'cancelled': return 'bg-red-50 text-red-700';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Workshops</h1>
            <p className="text-sm text-slate-500">Manage live sessions and workshops</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              leftIcon={<Sparkles className="w-4 h-4" />}
              onClick={() => setAiModalOpen(true)}
            >
              Generate with AI
            </Button>
            <Button size="sm" leftIcon={<Plus className="w-4 h-4" />} onClick={openCreate}>
              Create Workshop
            </Button>
          </div>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search workshops..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full sm:w-80 pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20"
          />
        </div>

        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-amber-500 mx-auto" />
          </div>
        ) : workshops.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-slate-100">
            <p className="text-slate-500">No workshops found</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="text-left text-xs font-bold text-slate-500 uppercase tracking-wider px-5 py-3">Workshop</th>
                    <th className="text-left text-xs font-bold text-slate-500 uppercase tracking-wider px-5 py-3">Duration</th>
                    <th className="text-left text-xs font-bold text-slate-500 uppercase tracking-wider px-5 py-3">Price</th>
                    <th className="text-left text-xs font-bold text-slate-500 uppercase tracking-wider px-5 py-3">Seats</th>
                    <th className="text-left text-xs font-bold text-slate-500 uppercase tracking-wider px-5 py-3">Status</th>
                    <th className="text-left text-xs font-bold text-slate-500 uppercase tracking-wider px-5 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {workshops.map((w) => (
                    <tr key={w.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-4">
                        <p className="text-sm font-semibold text-slate-900">{w.title}</p>
                        <p className="text-xs text-slate-500">{w.slug}</p>
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-600">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          {w.duration || '-'}
                        </div>
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-600">
                        <div className="flex items-center gap-1">
                          <IndianRupee className="w-3.5 h-3.5" />
                          {w.price}
                        </div>
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-600">
                        <div className="flex items-center gap-1">
                          <Users className="w-3.5 h-3.5" />
                          {w.seats_filled}/{w.max_seats}
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${statusColor(w.status)}`}>
                          {w.status}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-1">
                          <button onClick={() => navigate(`/admin/workshops/${w.id}`)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="View">
                            <Eye className="w-4 h-4" />
                          </button>
                          <button onClick={() => openEdit(w)} className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors">
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(w.id)}
                            disabled={deletingId === w.id}
                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            {deletingId === w.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4"
            onClick={() => setIsModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl"
            >
              <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                <h2 className="text-lg font-bold text-slate-900">
                  {editing ? 'Edit Workshop' : 'Create Workshop'}
                </h2>
                <button onClick={() => setIsModalOpen(false)} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <Input label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
                <Input label="Slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} required />
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Description</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    rows={3}
                    className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 resize-y"
                  />
                </div>
                <Input label="Duration" placeholder="e.g. 2 hours, 1 day" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} />
                <div className="grid grid-cols-2 gap-4">
                  <Input label="Start Date" type="datetime-local" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
                  <Input label="End Date" type="datetime-local" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Input label="Price (₹)" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
                  <Input label="Max Seats" type="number" value={form.max_seats} onChange={(e) => setForm({ ...form, max_seats: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Trainer</label>
                  <select
                    value={form.trainer_id}
                    onChange={(e) => setForm({ ...form, trainer_id: e.target.value })}
                    className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                  >
                    <option value="">Unassigned</option>
                    {trainers.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Status</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                  >
                    <option value="upcoming">Upcoming</option>
                    <option value="live">Live</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>
              <div className="p-6 border-t border-slate-100 flex justify-end gap-3">
                <Button variant="outline" onClick={() => setIsModalOpen(false)}>Cancel</Button>
                <Button onClick={handleSave} isLoading={saving}>
                  {editing ? 'Update' : 'Create'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* AI Generate Modal */}
      <AnimatePresence>
        {aiModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4"
            onClick={() => setAiModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl w-full max-w-md shadow-xl"
            >
              <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-500" />
                  Generate Workshop with AI
                </h2>
                <button onClick={() => setAiModalOpen(false)} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <p className="text-sm text-slate-500">
                  Enter a topic and our AI will generate a complete workshop outline for you.
                </p>
                <Input
                  label="Topic"
                  placeholder="e.g. AI for Credit Risk Assessment"
                  value={aiTopic}
                  onChange={(e) => setAiTopic(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAIGenerate()}
                />
              </div>
              <div className="p-6 border-t border-slate-100 flex justify-end gap-3">
                <Button variant="outline" onClick={() => setAiModalOpen(false)}>Cancel</Button>
                <Button onClick={handleAIGenerate} isLoading={aiLoading} leftIcon={<Sparkles className="w-4 h-4" />}>
                  Generate
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </AdminLayout>
  );
}
